/**
 * Shared picker component used by every context-aware add affordance:
 * append-after, insert-between, add-parallel, and the library drop. Always
 * shows the same three categories (Inputs / Analysis / Outputs).
 */

import React from 'react';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { INPUT_SUBTYPES, PRIMITIVES } from '~/pages/workflows/builder/fixtures/registry';
import {
  CATEGORY_ACCENT,
  CATEGORY_LABEL,
  INPUT_SUBTYPE_ICON,
  PRIMITIVE_ICON
} from '~/pages/workflows/builder/constants';
import type {
  InputSubtype,
  PrimitiveDefinition,
  TaskDefinitionKey
} from '~/pages/workflows/builder/types';

export interface NodePickerResult {
  taskKey: TaskDefinitionKey;
  /** Only set when the user picks a specific input subtype up-front. */
  inputSubtype?: InputSubtype;
}

export function NodePicker(props: {
  open: boolean;
  onClose: () => void;
  onPick: (result: NodePickerResult) => void;
  title?: string;
  subtitle?: string;
  /** Optional filter — e.g. only show primitives compatible with an upstream output. */
  allow?: (primitive: PrimitiveDefinition) => boolean;
  /** If true, show the 6 input subtypes as a compact pre-selection row. */
  showInputSubtypes?: boolean;
}) {
  const [search, setSearch] = React.useState('');

  // The Library shows one polymorphic "Input" card. Collapse the six INPUT_*
  // primitives into a single row in the picker too.
  const analysisAndOutput = PRIMITIVES.filter((p) => p.category !== 'input');

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return analysisAndOutput;
    return analysisAndOutput.filter(
      (p) =>
        p.label.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
    );
  }, [analysisAndOutput, search]);

  const byCategory = {
    analysis: filtered.filter((p) => p.category === 'analysis' && (!props.allow || props.allow(p))),
    output: filtered.filter((p) => p.category === 'output' && (!props.allow || props.allow(p)))
  };

  const onChoose = (result: NodePickerResult) => {
    props.onPick(result);
    setSearch('');
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/20 dark:bg-white/20" />
      <div className="fixed inset-0 flex items-start justify-center p-4 sm:p-16">
        <DialogPanel className="w-full max-w-2xl rounded-xl bg-white dark:bg-neutral-800 shadow-2xl ring-1 ring-neutral-900/10 dark:ring-white/10 overflow-hidden">
          <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-700 px-4 py-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400 dark:text-neutral-500" />
            <input
              autoFocus
              type="text"
              placeholder="Search primitives…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border-0 bg-transparent text-sm placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-0"
            />
            <kbd className="hidden sm:inline-flex items-center rounded border border-neutral-200 dark:border-neutral-700 px-1.5 py-0.5 text-xs text-neutral-400 dark:text-neutral-500">
              Esc
            </kbd>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-4 space-y-6">
            {(props.title || props.subtitle) && (
              <div className="space-y-0.5">
                {props.title && (
                  <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {props.title}
                  </div>
                )}
                {props.subtitle && (
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    {props.subtitle}
                  </div>
                )}
              </div>
            )}
            {props.showInputSubtypes && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Input
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {INPUT_SUBTYPES.map((st) => {
                    const Icon = INPUT_SUBTYPE_ICON[st.key];
                    return (
                      <button
                        key={st.key}
                        type="button"
                        onClick={() => onChoose({ taskKey: st.taskKey, inputSubtype: st.key })}
                        className="flex items-start gap-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 text-left hover:border-primary-300 dark:hover:border-primary-500/30 hover:bg-primary-50/40 dark:hover:bg-primary-500/10"
                      >
                        <span
                          className={clsx(
                            'flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset',
                            CATEGORY_ACCENT.input
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="flex-1">
                          <span className="block text-sm font-semibold text-neutral-900 dark:text-white">
                            {st.label}
                          </span>
                          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                            {st.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {(['analysis', 'output'] as const).map((cat) =>
              byCategory[cat].length > 0 ? (
                <div key={cat}>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {CATEGORY_LABEL[cat]}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {byCategory[cat].map((p) => {
                      const Icon = PRIMITIVE_ICON[p.key];
                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => onChoose({ taskKey: p.key })}
                          className="flex items-start gap-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 text-left hover:border-primary-300 dark:hover:border-primary-500/30 hover:bg-primary-50/40 dark:hover:bg-primary-500/10"
                        >
                          <span
                            className={clsx(
                              'flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset',
                              CATEGORY_ACCENT[p.category]
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="flex-1">
                            <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                              {p.label}
                            </span>
                            <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                              {p.description}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null
            )}
            {filtered.length === 0 && (!props.showInputSubtypes || search) && (
              <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 p-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                No primitives match "{search}".
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
